/** User preferences: everything a witch can toggle so the app pertains only to her practice. */

export const FEATURES = [
  "lunar",
  "sabbats",
  "eclipses",
  "retrogrades",
  "tarot",
  "astrology",
  "plants",
  "crystals",
  "oils",
  "recipes",
  "lessons",
] as const;
export type Feature = (typeof FEATURES)[number];

export const THEMES = ["clarity", "midnight"] as const;
export type Theme = (typeof THEMES)[number];

export type Hemisphere = "northern" | "southern";
export type SabbatDateStyle = "traditional" | "astronomical";

export interface Settings {
  theme: Theme;
  hemisphere: Hemisphere;
  sabbatDateStyle: SabbatDateStyle;
  reducedMotion: boolean;
  syncEnabled: boolean;
  features: Record<Feature, boolean>;
}

export const DEFAULT_SETTINGS: Settings = Object.freeze({
  theme: "clarity",
  hemisphere: "northern",
  sabbatDateStyle: "traditional",
  reducedMotion: false,
  syncEnabled: false,
  features: Object.freeze(
    Object.fromEntries(FEATURES.map((f) => [f, true])) as Record<Feature, boolean>,
  ),
}) as Settings;

const pick = <T extends string>(value: unknown, allowed: readonly T[], fallback: T): T =>
  typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;

const bool = (value: unknown, fallback: boolean) => (typeof value === "boolean" ? value : fallback);

/**
 * Turn any stored or incoming value into complete, valid settings.
 * Unknown keys are dropped and bad values fall back to defaults, so old clients never break new ones.
 */
export function normalizeSettings(input: unknown): Settings {
  const raw = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const rawFeatures = (
    raw.features && typeof raw.features === "object" ? raw.features : {}
  ) as Record<string, unknown>;
  return {
    theme: pick(raw.theme, THEMES, DEFAULT_SETTINGS.theme),
    hemisphere: pick(
      raw.hemisphere,
      ["northern", "southern"] as const,
      DEFAULT_SETTINGS.hemisphere,
    ),
    sabbatDateStyle: pick(
      raw.sabbatDateStyle,
      ["traditional", "astronomical"] as const,
      DEFAULT_SETTINGS.sabbatDateStyle,
    ),
    reducedMotion: bool(raw.reducedMotion, DEFAULT_SETTINGS.reducedMotion),
    syncEnabled: bool(raw.syncEnabled, DEFAULT_SETTINGS.syncEnabled),
    features: Object.fromEntries(FEATURES.map((f) => [f, bool(rawFeatures[f], true)])) as Record<
      Feature,
      boolean
    >,
  };
}

/** Apply a partial update to settings, returning a new normalized object. */
export function updateSettings(
  current: Settings,
  patch: Partial<Omit<Settings, "features">> & { features?: Partial<Record<Feature, boolean>> },
): Settings {
  return normalizeSettings({
    ...current,
    ...patch,
    features: { ...current.features, ...patch.features },
  });
}

/** Which content category feeds each library feature toggle. */
export const CATEGORY_FEATURE: Record<string, Feature> = {
  plant: "plants",
  crystal: "crystals",
  oil: "oils",
  recipe: "recipes",
  tarot_card: "tarot",
  zodiac_sign: "astrology",
  planet: "astrology",
  house: "astrology",
  aspect: "astrology",
  course: "lessons",
  lesson: "lessons",
  sabbat: "sabbats",
};

/** True when the user wants to see content of this category. Reference categories are always on. */
export function isCategoryEnabled(settings: Settings, category: string): boolean {
  const feature = CATEGORY_FEATURE[category];
  return feature ? settings.features[feature] : true;
}
